import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Scissors,
  Trees,
  Droplets,
  BrickWall,
  Snowflake,
  ChevronRight
} from 'lucide-react';
import { Button } from './ui/button';
import { images } from '../data/mock';

const featuredServices = [
  { id: 1, name: "Lawn Care", icon: Scissors, description: "Mowing, edging & maintenance" },
  { id: 2, name: "Landscaping", icon: Trees, description: "Design & installation" },
  { id: 3, name: "Hardscapes", icon: BrickWall, description: "Patios, walkways & walls" },
  { id: 4, name: "Irrigation", icon: Droplets, description: "Sprinkler systems & repair" },
  { id: 5, name: "Snow Removal", icon: Snowflake, description: "Winter property care" },
];

const Services = () => {
  const navigate = useNavigate();

  return (
    <section id="services" className="py-20 bg-bg-subtle">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 bg-brand-primary/10 border border-brand-primary/30 rounded-full px-4 py-2 mb-6">
            <span className="text-brand-dark text-sm font-semibold">
              What We Offer
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-6">
            Our Services
          </h2>
          <p className="text-text-secondary text-lg leading-relaxed">
            Professional lawn care and landscaping services for residential and commercial properties.
          </p>
        </div>

        {/* Featured Services - Clean Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-10">
          {featuredServices.map((service) => {
            const IconComponent = service.icon;
            return (
              <div
                key={service.id}
                className="group bg-bg-card rounded-2xl p-5 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 text-center"
              >
                <div className="w-14 h-14 bg-brand-primary/10 rounded-xl flex items-center justify-center mb-4 mx-auto group-hover:bg-brand-primary group-hover:scale-110 transition-all duration-300">
                  <IconComponent
                    className="text-brand-dark group-hover:text-white transition-colors"
                    size={26}
                  />
                </div>
                <h3 className="font-bold text-text-primary group-hover:text-brand-dark transition-colors mb-1">
                  {service.name}
                </h3>
                <p className="text-text-light text-sm hidden md:block">
                  {service.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* View All Button */}
        <div className="text-center mb-16">
          <Button
            onClick={() => navigate('/services')}
            className="bg-brand-dark text-white hover:bg-brand-hover rounded-full px-8 py-6 text-lg font-semibold inline-flex items-center gap-3 transition-all duration-300 hover:-translate-y-1 shadow-lg"
          >
            View All 18+ Services
            <ChevronRight size={22} />
          </Button>
        </div>

        {/* Featured Images */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="relative rounded-[32px] overflow-hidden h-[250px] md:h-[300px] group">
            <img
              src={images.hardscape}
              alt="Limestone Retaining Wall"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/80 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <h3 className="text-white text-xl md:text-2xl font-bold mb-2">
                Retaining Walls & Grading
              </h3>
              <p className="text-white/80 text-sm md:text-base">
                Expert limestone retaining walls and drainage solutions.
              </p>
            </div>
          </div>
          <div className="relative rounded-[32px] overflow-hidden h-[250px] md:h-[300px] group">
            <img
              src={images.lawnMower}
              alt="Landscape Lighting"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/80 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <h3 className="text-white text-xl md:text-2xl font-bold mb-2">
                Hardscapes & Outdoor Living
              </h3>
              <p className="text-white/80 text-sm md:text-base">
                Beautiful patios and walkways with landscape lighting.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
